'use client'
import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'

export default function TankDuelArcade() {
  const [inLobby, setInLobby] = useState(true)
  const [roomName, setRoomName] = useState('arena-1')
  const [tankColor, setTankColor] = useState('#6366f1')
  const [tankClass, setTankClass] = useState('vanguard')
  const [hallOfFame, setHallOfFame] = useState<any[]>([])

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const [connected, setConnected] = useState(false)
  const [playersList, setPlayersList] = useState<any[]>([])
  const [isDeadUI, setIsDeadUI] = useState(false)
  const [matchStatus, setMatchStatus] = useState('waiting')
  const [timeLeft, setTimeLeft] = useState(120)
  const [countdownLeft, setCountdownLeft] = useState(5)
  const [restartIn, setRestartIn] = useState(0)
  const [abandonLeft, setAbandonLeft] = useState(0)

  const [myCoins, setMyCoins] = useState(0)
  const [myUpgrades, setMyUpgrades] = useState({ damage: 0, rapidFire: 0, extraSpeed: 0 })
  
  const wsRef = useRef<WebSocket | null>(null)
  const myIdRef = useRef<string | null>(null)
  const isDeadRef = useRef<boolean>(false)
  const matchStatusRef = useRef<string>('waiting') 
  
  const playersRef = useRef<any>({})
  const bulletsRef = useRef<any[]>([])
  const obstaclesRef = useRef<any[]>([])
  const hazardsRef = useRef<any[]>([])
  const itemsRef = useRef<any[]>([])
  const myPosRef = useRef({ x: 100, y: 100, angle: 0 })

  const particlesRef = useRef<any[]>([])
  const shakeTimerRef = useRef<number>(0)

  // Mobile control state ref to prevent re-triggering render loops
  const mobileKeysRef = useRef({ up: false, down: false, left: false, right: false })

  // Fetch Hall of Fame on load
  useEffect(() => {
    fetch('/api/arcade/tanks/leaderboard')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setHallOfFame(data) })
      .catch(() => {})
  }, [])

  const isColliding = (x: number, y: number, obstacles: any[]) => {
    const halfW = 16
    const halfH = 12
    const tankLeft = x - halfW
    const tankRight = x + halfW
    const tankTop = y - halfH
    const tankBottom = y + halfH

    for (const obs of obstacles) {
      if (
        tankRight > obs.x &&
        tankLeft < obs.x + obs.width &&
        tankBottom > obs.y &&
        tankTop < obs.y + obs.height
      ) {
        for (const cell of obs.cells) {
          if (!cell.alive) continue
          const cellWorldX = obs.x + cell.localX
          const cellWorldY = obs.y + cell.localY
          if (
            tankRight > cellWorldX &&
            tankLeft < cellWorldX + cell.size &&
            tankBottom > cellWorldY &&
            tankTop < cellWorldY + cell.size
          ) {
            return true
          }
        }
      }
    }
    return false
  }

  const spawnExplosion = (x: number, y: number, color: string, count = 16) => {
    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 2
      const speed = Math.random() * 5 + 1
      particlesRef.current.push({
        x, y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        alpha: 1,
        color,
        size: Math.random() * 4 + 2
      })
    }
  }

  const joinGame = (e: React.FormEvent) => {
    e.preventDefault()
    setInLobby(false)
  }

  const buyUpgrade = (upgradeType: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'buy_upgrade', upgrade: upgradeType }))
    }
  }

  const handleShootAction = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && !isDeadRef.current && matchStatusRef.current === 'playing') {
      wsRef.current.send(JSON.stringify({ type: 'shoot' }))
    }
  }

  const setMobileKey = (key: 'up' | 'down' | 'left' | 'right', active: boolean) => {
    mobileKeysRef.current[key] = active
  }

  useEffect(() => {
    if (inLobby) return

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const wsUrl = `${protocol}//${window.location.hostname}/tanks-ws/`
    const ws = new WebSocket(wsUrl)
    wsRef.current = ws

    ws.onopen = () => {
      setConnected(true)
      ws.send(JSON.stringify({ type: 'join', room: roomName, color: tankColor, tankClass }))
    }

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data)
      if (data.type === 'welcome') {
        myIdRef.current = data.playerId
      }
      if (data.type === 'state') {
        const oldPlayers = playersRef.current
        playersRef.current = data.players || {}
        bulletsRef.current = data.bullets || []
        obstaclesRef.current = data.obstacles || []
        hazardsRef.current = data.hazards || []
        itemsRef.current = data.items || []
        
        setMatchStatus(data.status || 'waiting')
        matchStatusRef.current = data.status || 'waiting'
        
        setTimeLeft(data.timeLeft || 0)
        setCountdownLeft(data.countdownLeft || 0)
        setRestartIn(data.restartIn || 0)
        setAbandonLeft(data.abandonLeft || 0)
        setPlayersList(Object.entries(data.players || {}).map(([id, p]: any) => ({ id, ...p })))

        if (myIdRef.current && data.players[myIdRef.current]) {
          const serverPlayer = data.players[myIdRef.current]
          
          if (!serverPlayer.isDead) {
            myPosRef.current.x = serverPlayer.x
            myPosRef.current.y = serverPlayer.y
          }

          setMyCoins(serverPlayer.coins || 0)
          setMyUpgrades(serverPlayer.upgrades || { damage: 0, rapidFire: 0, extraSpeed: 0 })

          if (oldPlayers[myIdRef.current] && serverPlayer.health < oldPlayers[myIdRef.current].health) {
            shakeTimerRef.current = 18
            spawnExplosion(serverPlayer.x, serverPlayer.y, '#ef4444', 12)
          }

          isDeadRef.current = serverPlayer.isDead
          setIsDeadUI(serverPlayer.isDead)
        }
      }
    }

    ws.onclose = () => setConnected(false)

    const keys: any = {}
    const handleKeyDown = (e: KeyboardEvent) => { 
      keys[e.code] = true 
      keys[e.key] = true 

      if (e.code === 'Space') {
        handleShootAction()
      }
    }
    const handleKeyUp = (e: KeyboardEvent) => { 
      keys[e.code] = false 
      keys[e.key] = false 
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    let animationFrameId: number
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')

    const render = () => {
      if (!ctx) return

      const myPlayer = myIdRef.current ? playersRef.current[myIdRef.current] : null

      if (myPlayer && !myPlayer.isDead && matchStatusRef.current === 'playing') {
        const now = Date.now()
        
        let classSpeed = 3
        if (myPlayer.tankClass === 'juggernaut') classSpeed = 2.1
        if (myPlayer.tankClass === 'phantom') classSpeed = 4.2

        let baseSpeed = classSpeed + ((myPlayer.upgrades?.extraSpeed || 0) * 0.75)
        if (myPlayer.inHazard) baseSpeed *= 0.5

        const speed = (myPlayer.speedBuffUntil && now < myPlayer.speedBuffUntil) ? baseSpeed + 1.5 : baseSpeed
        
        let nextX = myPosRef.current.x
        let nextY = myPosRef.current.y

        const isUp = keys['ArrowUp'] || keys['KeyW'] || keys['w'] || mobileKeysRef.current.up
        const isDown = keys['ArrowDown'] || keys['KeyS'] || keys['s'] || mobileKeysRef.current.down
        const isLeft = keys['ArrowLeft'] || keys['KeyA'] || keys['a'] || mobileKeysRef.current.left
        const isRight = keys['ArrowRight'] || keys['KeyD'] || keys['d'] || mobileKeysRef.current.right

        if (isUp) {
          nextY -= speed
          myPosRef.current.angle = -Math.PI / 2
        } else if (isDown) {
          nextY += speed
          myPosRef.current.angle = Math.PI / 2
        } else if (isLeft) {
          nextX -= speed
          myPosRef.current.angle = Math.PI
        } else if (isRight) {
          nextX += speed
          myPosRef.current.angle = 0
        }

        if (!isColliding(nextX, nextY, obstaclesRef.current)) {
          myPosRef.current.x = Math.max(16, Math.min(800 - 16, nextX))
          myPosRef.current.y = Math.max(12, Math.min(500 - 12, nextY))
        }

        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'update', state: myPosRef.current }))
        }
      }

      ctx.save()

      if (shakeTimerRef.current > 0) {
        shakeTimerRef.current--
        const offsetX = (Math.random() - 0.5) * 8
        const offsetY = (Math.random() - 0.5) * 8
        ctx.translate(offsetX, offsetY)
      }

      const bgGrad = ctx.createRadialGradient(400, 250, 50, 400, 250, 600)
      bgGrad.addColorStop(0, '#090d1e')
      bgGrad.addColorStop(1, '#020408')
      ctx.fillStyle = bgGrad
      ctx.fillRect(0, 0, canvas.width, canvas.height)

      ctx.strokeStyle = '#1e293b44'
      ctx.lineWidth = 1
      for (let x = 0; x < canvas.width; x += 40) {
        ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, canvas.height); ctx.stroke()
      }
      for (let y = 0; y < canvas.height; y += 40) {
        ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(canvas.width, y); ctx.stroke()
      }

      // Render Hazard Sludge Pools
      const hazards = hazardsRef.current
      for (const h of hazards) {
        ctx.shadowBlur = 15
        ctx.shadowColor = '#10b981'
        ctx.fillStyle = '#064e3b66'
        ctx.fillRect(h.x, h.y, h.width, h.height)
        ctx.strokeStyle = '#059669'
        ctx.lineWidth = 2
        ctx.strokeRect(h.x, h.y, h.width, h.height)
        ctx.shadowBlur = 0
      }

      const obstacles = obstaclesRef.current
      for (const obs of obstacles) {
        ctx.shadowBlur = 10
        ctx.shadowColor = '#0f172a'
        ctx.fillStyle = '#1e293b'
        for (const cell of obs.cells) {
          if (!cell.alive) continue
          ctx.fillRect(obs.x + cell.localX, obs.y + cell.localY, cell.size, cell.size)
        }
        ctx.shadowBlur = 0
      }

      const items = itemsRef.current
      const time = Date.now() / 200
      for (const item of items) {
        const floatOffset = Math.sin(time + item.x) * 4
        ctx.save()
        ctx.translate(item.x, item.y + floatOffset)

        if (item.type === 'heal') {
          ctx.shadowBlur = 15; ctx.shadowColor = '#10b981'
          ctx.fillStyle = '#10b981'
          ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.fill()
          ctx.fillStyle = '#ffffff'
          ctx.fillRect(-8, -3, 16, 6); ctx.fillRect(-3, -8, 6, 16)
        } else if (item.type === 'speed') {
          ctx.shadowBlur = 15; ctx.shadowColor = '#f59e0b'
          ctx.fillStyle = '#f59e0b'
          ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.fill()
          ctx.fillStyle = '#000000'
          ctx.font = 'bold 12px monospace'
          ctx.fillText('⚡', -6, 4)
        } else if (item.type === 'shield') {
          ctx.shadowBlur = 15; ctx.shadowColor = '#3b82f6'
          ctx.fillStyle = '#3b82f6'
          ctx.beginPath(); ctx.arc(0, 0, 14, 0, Math.PI * 2); ctx.fill()
          ctx.fillStyle = '#ffffff'
          ctx.font = 'bold 10px monospace'
          ctx.fillText('🛡️', -7, 4)
        } else if (item.type === 'coin') {
          ctx.shadowBlur = 15; ctx.shadowColor = '#eab308'
          ctx.fillStyle = '#eab308'
          ctx.beginPath(); ctx.arc(0, 0, 12, 0, Math.PI * 2); ctx.fill()
          ctx.strokeStyle = '#fef08a'; ctx.lineWidth = 2; ctx.stroke()
          ctx.fillStyle = '#713f12'
          ctx.font = 'bold 11px monospace'
          ctx.fillText('$', -3, 4)
        }
        ctx.restore()
      }

      const players = playersRef.current
      for (const id in players) {
        const p = players[id]
        if (p.isDead) continue

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.angle || 0)

        ctx.fillStyle = '#1e293b'
        ctx.fillRect(-18, -14, 36, 6)
        ctx.fillRect(-18, 8, 36, 6)

        ctx.shadowBlur = 10
        ctx.shadowColor = p.color || '#10b981'
        ctx.fillStyle = p.color || '#10b981'
        ctx.fillRect(-14, -10, 28, 20)

        ctx.fillStyle = '#ffffff33'
        ctx.fillRect(-10, -6, 20, 12)

        ctx.fillStyle = '#cbd5e1'
        ctx.fillRect(0, -4, 22, 8)

        ctx.beginPath()
        ctx.arc(0, 0, 7, 0, Math.PI * 2)
        ctx.fillStyle = '#475569'
        ctx.fill()
        ctx.lineWidth = 2
        ctx.strokeStyle = '#94a3b8'
        ctx.stroke()

        ctx.restore()

        if (p.shieldUntil && Date.now() < p.shieldUntil) {
          ctx.shadowBlur = 20
          ctx.shadowColor = '#3b82f6'
          ctx.strokeStyle = '#60a5fa'
          ctx.lineWidth = 3
          ctx.beginPath()
          ctx.arc(p.x, p.y, 26, 0, Math.PI * 2)
          ctx.stroke()
          ctx.shadowBlur = 0
        }

        const barWidth = 36
        const healthPercent = Math.max(0, p.health / p.maxHealth)
        ctx.fillStyle = '#0f172a'
        ctx.fillRect(p.x - barWidth / 2, p.y - 32, barWidth, 5)
        ctx.fillStyle = healthPercent > 0.5 ? '#10b981' : healthPercent > 0.25 ? '#f59e0b' : '#ef4444'
        ctx.fillRect(p.x - barWidth / 2, p.y - 32, barWidth * healthPercent, 5)

        ctx.fillStyle = '#94a3b8'
        ctx.font = 'bold 9px monospace'
        ctx.textAlign = 'center'
        ctx.fillText(id === myIdRef.current ? 'YOU' : `TANK #${id.slice(0, 4)}`, p.x, p.y - 38)
      }

      const bullets = bulletsRef.current
      ctx.shadowBlur = 12
      ctx.shadowColor = '#f59e0b'
      ctx.fillStyle = '#fbbf24'
      for (const b of bullets) {
        ctx.beginPath()
        ctx.arc(b.x, b.y, 4, 0, Math.PI * 2)
        ctx.fill()
      }
      ctx.shadowBlur = 0

      particlesRef.current.forEach(pt => {
        pt.x += pt.vx
        pt.y += pt.vy
        pt.alpha -= 0.04
        ctx.fillStyle = pt.color
        ctx.globalAlpha = Math.max(0, pt.alpha)
        ctx.fillRect(pt.x, pt.y, pt.size, pt.size)
        ctx.globalAlpha = 1.0
      })
      particlesRef.current = particlesRef.current.filter(pt => pt.alpha > 0)

      ctx.restore() 

      animationFrameId = requestAnimationFrame(render)
    }

    render()

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
      cancelAnimationFrame(animationFrameId)
      ws.close()
    }
  }, [inLobby])

  if (inLobby) {
    return (
      <div className="max-w-md mx-auto space-y-6 font-mono text-xs p-6 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl mt-12">
        <div className="space-y-2 text-center">
          <h1 className="text-white font-bold text-base">🕹️ Tank Duel Lobby</h1>
          <p className="text-slate-400 text-[11px]">Select your class, room, and color to deploy into battle.</p>
        </div>

        <form onSubmit={joinGame} className="space-y-4">
          <div className="space-y-1">
            <label className="text-slate-300 font-bold">Room Code / Lobby Name</label>
            <input 
              type="text" 
              value={roomName} 
              onChange={(e) => setRoomName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
              required
              className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-white focus:outline-none focus:border-indigo-500"
              placeholder="e.g. arena-1, 1v1-pro"
            />
          </div>

          <div className="space-y-2">
            <label className="text-slate-300 font-bold">Select Tank Class</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'vanguard', name: 'Vanguard', desc: 'Balanced 100 HP' },
                { id: 'juggernaut', name: 'Juggernaut', desc: 'Heavy 160 HP' },
                { id: 'phantom', name: 'Phantom', desc: 'Fast 75 HP' }
              ].map(cls => (
                <button
                  key={cls.id}
                  type="button"
                  onClick={() => setTankClass(cls.id)}
                  className={`p-2.5 rounded-xl border text-left transition-all ${tankClass === cls.id ? 'bg-indigo-600/20 border-indigo-500 text-white' : 'bg-slate-950 border-slate-800 text-slate-400'}`}
                >
                  <div className="font-bold">{cls.name}</div>
                  <div className="text-[9px] text-slate-500">{cls.desc}</div>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1">
            <label className="text-slate-300 font-bold">Tank Color Accent</label>
            <div className="flex gap-3 pt-1">
              {['#6366f1', '#10b981', '#f59e0b', '#ec4899', '#3b82f6'].map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setTankColor(c)}
                  className={`w-8 h-8 rounded-lg border-2 transition-all ${tankColor === c ? 'border-white scale-110 shadow-lg' : 'border-transparent opacity-60'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <button 
            type="submit" 
            className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3 rounded-xl shadow-lg transition-all"
          >
            Deploy Into Arena &rarr;
          </button>
        </form>

        {/* Hall of Fame Preview in Lobby */}
        <div className="border-t border-slate-800 pt-4 space-y-2">
          <div className="text-[10px] text-amber-400 font-bold uppercase tracking-widest">🏆 All-Time Hall of Fame</div>
          <div className="space-y-1">
            {hallOfFame.map((entry, idx) => (
              <div key={entry.name} className="flex justify-between items-center bg-slate-950 p-2 rounded-lg border border-slate-800">
                <span className="text-slate-300">#{idx + 1} {entry.name}</span>
                <span className="text-emerald-400 font-black">{entry.kills} Kills</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center pt-2">
          <Link href="/arcade" className="text-slate-500 hover:text-slate-300">
            &larr; Back to Arcade Hub
          </Link>
        </div>
      </div>
    )
  }

  const formatTime = (secs: number) => {
    const mins = Math.floor(secs / 60)
    const remainSecs = secs % 60
    return `${mins}:${remainSecs < 10 ? '0' : ''}${remainSecs}`
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 font-mono text-xs p-4 select-none">
      <div className="flex items-center justify-between bg-slate-900 border border-slate-800 p-4 rounded-xl">
        <div>
          <h1 className="text-white font-bold text-sm">🕹️ Room: <span className="text-indigo-400">{roomName}</span></h1>
          <p className="text-slate-400 text-[11px]">Match Timer: <span className={`font-bold ${timeLeft <= 10 && matchStatus === 'playing' ? 'text-rose-500 animate-pulse text-sm' : 'text-emerald-400'}`}>{formatTime(timeLeft)}</span> &bull; Hazards Active 🟢</p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-2 py-1 rounded text-[10px] font-bold ${connected ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {connected ? '● LIVE SYNC' : '○ DISCONNECTED'}
          </span>
          <button onClick={() => setInLobby(true)} className="bg-slate-800 hover:bg-slate-700 text-slate-200 px-3 py-1.5 rounded-lg">
            Change Room
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3 bg-slate-950 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col items-center p-4 relative">
          <canvas 
            ref={canvasRef} 
            width={800} 
            height={500} 
            className="bg-[#020408] border border-slate-800 rounded-xl shadow-inner max-w-full h-auto"
          />

          {matchStatus === 'playing' && timeLeft <= 10 && (
            <div className="absolute top-4 bg-rose-500/90 text-white font-black px-6 py-2 rounded-xl shadow-2xl animate-pulse z-10 uppercase tracking-widest text-sm border border-rose-400">
              ⚠️ {timeLeft} Seconds Remaining!
            </div>
          )}

          {matchStatus === 'playing' && abandonLeft > 0 && (
            <div className="absolute top-4 bg-amber-500/95 text-slate-950 font-black px-6 py-2 rounded-xl shadow-2xl animate-pulse z-10 uppercase tracking-widest text-sm border border-amber-400">
              ⚠️ Everyone left! Game aborting in {abandonLeft}s...
            </div>
          )}

          {matchStatus === 'waiting' && (
            <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm flex flex-col items-center justify-center space-y-3 z-20">
              <div className="text-amber-400 font-black text-xl uppercase tracking-widest animate-pulse">⏳ Waiting for Players</div>
              <div className="text-slate-400 text-xs">Need at least 2 combatants in room <span className="text-indigo-400 font-bold">'{roomName}'</span> to start.</div>
            </div>
          )}

          {matchStatus === 'countdown' && (
            <div className="absolute inset-0 bg-slate-950/90 backdrop-blur-sm flex flex-col items-center justify-center space-y-3 z-20">
              <div className="text-indigo-400 font-black text-2xl uppercase tracking-widest">🚀 Match Starting Soon</div>
              <div className="text-emerald-400 font-black text-4xl animate-bounce">{countdownLeft}s</div>
            </div>
          )}

          {isDeadUI && matchStatus === 'playing' && (
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center space-y-3 z-20">
              <div className="text-rose-500 font-black text-xl uppercase tracking-widest">💥 Tank Destroyed</div>
              <div className="text-slate-400 text-xs">Deploying to a secure sector... Respawning shortly.</div>
            </div>
          )}

          {matchStatus === 'ended' && (
            <div className="absolute inset-0 bg-slate-950/95 backdrop-blur-md flex flex-col items-center justify-center space-y-6 z-30 p-6 text-center">
              <div className="space-y-1">
                <h2 className="text-indigo-400 font-black text-2xl uppercase tracking-widest">🏁 Match Concluded</h2>
                <p className="text-slate-400 text-xs">Final Leaderboard Standings (Upgrades Reset)</p>
              </div>

              <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden p-4 space-y-2">
                {playersList
                  .sort((a, b) => (b.kills || 0) - (a.kills || 0))
                  .map((p, idx) => (
                    <div key={p.id} className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-slate-400">#{idx + 1}</span>
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }}></span>
                        <span className="text-slate-200 font-bold">{p.id === myIdRef.current ? 'You' : `Tank #${p.id.slice(0, 4)}`}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-emerald-400 font-black">{p.kills || 0} Kills</span>
                        <span className="text-slate-500 mx-2">/</span>
                        <span className="text-rose-400 font-bold">{p.deaths || 0} Deaths</span>
                      </div>
                    </div>
                  ))}
              </div>

              <div className="text-slate-400 text-xs animate-pulse">
                New match starting in <span className="text-emerald-400 font-bold">{restartIn}s</span>...
              </div>
            </div>
          )}

          {/* --- MOBILE TOUCH CONTROLS DECK --- */}
          <div className="w-full mt-4 flex items-center justify-between bg-slate-900/80 border border-slate-800 p-4 rounded-xl">
            
            {/* D-Pad Controls */}
            <div className="relative w-36 h-36 bg-slate-950/80 border border-slate-800 rounded-full flex items-center justify-center shadow-inner">
              {/* UP */}
              <button
                onTouchStart={() => setMobileKey('up', true)}
                onTouchEnd={() => setMobileKey('up', false)}
                onMouseDown={() => setMobileKey('up', true)}
                onMouseUp={() => setMobileKey('up', false)}
                onMouseLeave={() => setMobileKey('up', false)}
                className="absolute top-1 w-11 h-11 bg-slate-800 hover:bg-indigo-600 active:bg-indigo-500 text-white rounded-t-xl font-bold flex items-center justify-center transition-colors shadow"
              >
                ▲
              </button>
              {/* DOWN */}
              <button
                onTouchStart={() => setMobileKey('down', true)}
                onTouchEnd={() => setMobileKey('down', false)}
                onMouseDown={() => setMobileKey('down', true)}
                onMouseUp={() => setMobileKey('down', false)}
                onMouseLeave={() => setMobileKey('down', false)}
                className="absolute bottom-1 w-11 h-11 bg-slate-800 hover:bg-indigo-600 active:bg-indigo-500 text-white rounded-b-xl font-bold flex items-center justify-center transition-colors shadow"
              >
                ▼
              </button>
              {/* LEFT */}
              <button
                onTouchStart={() => setMobileKey('left', true)}
                onTouchEnd={() => setMobileKey('left', false)}
                onMouseDown={() => setMobileKey('left', true)}
                onMouseUp={() => setMobileKey('left', false)}
                onMouseLeave={() => setMobileKey('left', false)}
                className="absolute left-1 w-11 h-11 bg-slate-800 hover:bg-indigo-600 active:bg-indigo-500 text-white rounded-l-xl font-bold flex items-center justify-center transition-colors shadow"
              >
                ◀
              </button>
              {/* RIGHT */}
              <button
                onTouchStart={() => setMobileKey('right', true)}
                onTouchEnd={() => setMobileKey('right', false)}
                onMouseDown={() => setMobileKey('right', true)}
                onMouseUp={() => setMobileKey('right', false)}
                onMouseLeave={() => setMobileKey('right', false)}
                className="absolute right-1 w-11 h-11 bg-slate-800 hover:bg-indigo-600 active:bg-indigo-500 text-white rounded-r-xl font-bold flex items-center justify-center transition-colors shadow"
              >
                ▶
              </button>
              {/* Center Pivot */}
              <div className="w-8 h-8 bg-slate-900 rounded-full border border-slate-800"></div>
            </div>

            {/* Fire Action Button */}
            <div className="flex items-center justify-center pr-4">
              <button
                onTouchStart={handleShootAction}
                onClick={handleShootAction}
                className="w-24 h-24 bg-gradient-to-tr from-rose-600 to-amber-500 hover:from-rose-500 hover:to-amber-400 active:scale-95 text-white font-black text-sm rounded-full shadow-lg shadow-rose-600/30 border-2 border-rose-400 flex items-center justify-center tracking-widest uppercase transition-all cursor-pointer"
              >
                FIRE 🔥
              </button>
            </div>

          </div>

        </div>

        <div className="lg:col-span-1 space-y-4 flex flex-col">
          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 space-y-3 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-2">
              <span className="text-[10px] text-amber-400 font-bold uppercase tracking-widest">⚡ In-Match Armory</span>
              <span className="text-amber-300 font-black">🪙 {myCoins}</span>
            </div>

            <div className="space-y-2">
              <div className="p-2.5 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-between">
                <div>
                  <div className="text-white font-bold">Cannon Power</div>
                  <div className="text-[9px] text-slate-400">+{myUpgrades.damage * 10} Dmg (Lvl {myUpgrades.damage}/3)</div>
                </div>
                <button 
                  onClick={() => buyUpgrade('damage')}
                  disabled={myCoins < 40 || myUpgrades.damage >= 3 || matchStatus !== 'playing'}
                  className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white px-2.5 py-1 rounded-lg font-bold"
                >
                  40 🪙
                </button>
              </div>

              <div className="p-2.5 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-between">
                <div>
                  <div className="text-white font-bold">Rapid Fire</div>
                  <div className="text-[9px] text-slate-400">Fire Rate (Lvl {myUpgrades.rapidFire}/3)</div>
                </div>
                <button 
                  onClick={() => buyUpgrade('rapidFire')}
                  disabled={myCoins < 50 || myUpgrades.rapidFire >= 3 || matchStatus !== 'playing'}
                  className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white px-2.5 py-1 rounded-lg font-bold"
                >
                  50 🪙
                </button>
              </div>

              <div className="p-2.5 bg-slate-900 border border-slate-800 rounded-xl flex items-center justify-between">
                <div>
                  <div className="text-white font-bold">Treads Speed</div>
                  <div className="text-[9px] text-slate-400">Agility (Lvl {myUpgrades.extraSpeed}/3)</div>
                </div>
                <button 
                  onClick={() => buyUpgrade('extraSpeed')}
                  disabled={myCoins < 35 || myUpgrades.extraSpeed >= 3 || matchStatus !== 'playing'}
                  className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:hover:bg-indigo-600 text-white px-2.5 py-1 rounded-lg font-bold"
                >
                  35 🪙
                </button>
              </div>
            </div>
          </div>

          <div className="bg-slate-950 border border-slate-800 rounded-2xl p-4 flex flex-col space-y-3 shadow-2xl flex-1">
            <div className="text-[10px] text-indigo-400 font-bold uppercase tracking-widest border-b border-slate-800 pb-2 flex justify-between items-center">
              <span>🏆 Scoreboard</span>
              <span className={`font-bold ${timeLeft <= 10 && matchStatus === 'playing' ? 'text-rose-500 animate-pulse' : 'text-emerald-400'}`}>{formatTime(timeLeft)}</span>
            </div>
            <div className="space-y-2 flex-1 overflow-y-auto">
              {playersList.length === 0 ? (
                <div className="text-slate-500 text-center py-6">No combatants online</div>
              ) : (
                playersList
                  .sort((a, b) => (b.kills || 0) - (a.kills || 0))
                  .map((p) => (
                    <div key={p.id} className={`p-3 border rounded-xl flex items-center justify-between ${p.id === myIdRef.current ? 'bg-indigo-950/40 border-indigo-500/50' : 'bg-slate-900 border-slate-800'}`}>
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }}></span>
                        <span className="text-slate-300 font-bold">
                          {p.id === myIdRef.current ? 'You' : `Tank #${p.id.slice(0, 4)}`}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="text-emerald-400 font-black">{p.kills || 0}</span> 
                        <span className="text-slate-500 mx-1">/</span> 
                        <span className="text-rose-400 font-bold">{p.deaths || 0}</span>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}